import { cn } from '@/lib/utils'
import type { PresentTextData } from '@/hooks/useVisualTools'

interface PresentTextCardProps {
  data: PresentTextData
  onClose: () => void
  className?: string
}

const categoryLabels: Record<string, string> = {
  vocabulary: 'Vocabulary',
  grammar: 'Grammar',
  phrase: 'Phrase',
  example: 'Example',
}

export function PresentTextCard({ data, onClose, className }: PresentTextCardProps) {
  const label = categoryLabels[data.category] || 'Vocabulary'

  return (
    <div
      className={cn(
        'rounded-xl border p-5 shadow-lg animate-fade-in',
        'bg-gradient-to-br from-amber-50 to-orange-50',
        'border-border',
        className
      )}
    >
      {/* Header with category badge and close button */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
          {label}
        </span>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-black/5"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      {/* Main text */}
      <p className="text-2xl font-bold text-foreground mb-2">{data.text}</p>

      {/* Translation */}
      {data.translation && (
        <p className="text-base text-muted-foreground mb-2">
          {data.translation}
        </p>
      )}

      {/* Example sentence */}
      {data.example && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <p className="text-sm text-muted-foreground italic">
            "{data.example}"
          </p>
        </div>
      )}
    </div>
  )
}
